import {
  type AccountsState,
  getActiveAccount,
  type Session,
  type StoredAccount,
  VISITOR_ID,
} from "@fieldcheck/shared";
import { readLastActiveAccountId, readTabAccountId, writeTabAccountId } from "@/lib/tab-account";

export function defaultAccountId(state: AccountsState): string | null {
  if (state.accounts.length === 0) {
    return null;
  }
  const remembered = readLastActiveAccountId();
  if (remembered !== null && state.accounts.some((a) => a.userId === remembered)) {
    return remembered;
  }
  const active = getActiveAccount(state);
  if (active) {
    return active.userId;
  }
  return state.accounts[0]?.userId ?? null;
}

export function resolveTabAccount(
  state: AccountsState,
  tabAccountId: string | null = readTabAccountId(),
): StoredAccount | null {
  if (state.accounts.length === 0) {
    return null;
  }
  if (tabAccountId !== null) {
    const pinned = state.accounts.find((a) => a.userId === tabAccountId);
    if (pinned) {
      return pinned;
    }
  }
  const fallbackId = defaultAccountId(state);
  return state.accounts.find((a) => a.userId === fallbackId) ?? null;
}

export function ensureTabAccount(state: AccountsState): string | null {
  const current = readTabAccountId();
  if (current !== null && state.accounts.some((a) => a.userId === current)) {
    return current;
  }
  const resolved = defaultAccountId(state);
  if (resolved === null) {
    return null;
  }
  writeTabAccountId(resolved);
  return resolved;
}

export function resolvePerTabSyncUserId(
  state: AccountsState,
  tabAccountId: string | null,
): string | null {
  const account = resolveTabAccount(state, tabAccountId);
  if (account === null) {
    return null;
  }
  return account.kind === "guest" ? VISITOR_ID : account.userId;
}

export function indexOfUserId(state: AccountsState, userId: string): number | null {
  const idx = state.accounts.findIndex((a) => a.userId === userId);
  return idx === -1 ? null : idx;
}

export function decodeJwtClaims(token: string | null | undefined): object | null {
  if (!token) {
    return null;
  }
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) {
    return null;
  }
  try {
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = globalThis.atob(padded);
    const decoded: unknown = JSON.parse(json);
    return typeof decoded === "object" && decoded !== null ? decoded : null;
  } catch {
    return null;
  }
}

export function decodeJwtRole(token: string | null | undefined): string | null {
  const claims = decodeJwtClaims(token);
  if (claims === null) {
    return null;
  }
  const role: unknown = Reflect.get(claims, "role");
  return typeof role === "string" ? role : null;
}

export interface ActiveAccountView {
  account: StoredAccount | null;
  isGuest: boolean;
  isAuthenticated: boolean;
  role: string | null;
  user: Session | undefined;
}

export function resolveActiveAccountView(
  state: AccountsState,
  tabAccountId: string | null = readTabAccountId(),
  globalSession?: Session | null | undefined,
): ActiveAccountView {
  const account = resolveTabAccount(state, tabAccountId);

  if (account === null) {
    const fallback = globalSession ?? undefined;
    return {
      account: null,
      isGuest: !fallback,
      isAuthenticated: Boolean(fallback),
      role: fallback?.user.role ?? null,
      user: fallback,
    };
  }

  if (account.kind === "guest") {
    return { account, isGuest: true, isAuthenticated: false, role: null, user: undefined };
  }

  if (globalSession && globalSession.user.id === account.userId) {
    const role = globalSession.user.role ?? account.role ?? decodeJwtRole(account.token);
    const user =
      role === globalSession.user.role
        ? globalSession
        : { ...globalSession, user: { ...globalSession.user, role } };
    return { account, isGuest: false, isAuthenticated: true, role, user };
  }

  const role = account.role ?? decodeJwtRole(account.token);
  const user: Session = {
    user: {
      id: account.userId,
      email: account.email,
      name: account.displayName,
      image: account.avatar ?? null,
      role,
    },
    session: { id: "", userId: account.userId, expiresAt: "", token: account.token ?? "" },
  };
  return { account, isGuest: false, isAuthenticated: true, role, user };
}
